import React, { useEffect, useRef, useState } from "react";
import { Button, Box, IconButton } from "@mui/material";
import { getReportsData } from "../../Services/ReportApi";
import { Bar, Pie } from "react-chartjs-2";
import { useLocation, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CountUp from "react-countup";
import { motion } from "framer-motion";
import Grid from "@mui/material/Grid2";
import { ReactComponent as ArrowbackIcon } from "../../Assets/arrowback.svg";

import {
  Chart as ChartJS,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { text } from "d3";

ChartJS.register(
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

type ReportItem = {
  log_date: string;
  click_count: number;
  close_count: number;
  impression_count: number;
};

function formatDate(log_date: string, useMonthOnly: boolean = false): string {
  const date = new Date(log_date);
  return useMonthOnly
    ? date.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) // 1 Apr
    : date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }); // 01 Apr
}

function Report() {
  const [reportData, setReportData] = useState<ReportItem[]>([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date;
  });
  const [toDate, setToDate] = useState<Date>(new Date());
  const location = useLocation();
  const hasFetched = useRef(false);
  const [experiencName, setExperiencName] = useState("");
  const [maxToDate, setMaxToDate] = useState<Date>(new Date());

  const fetchReport = async () => {
    try {
      const { id, name } = location.state || { id: 0, name: "" };

      if (id === 0) throw new Error("Please select an experience.");
      setExperiencName(name);
      const formattedFrom = fromDate.toISOString().split("T")[0];
      const formattedTo = toDate.toISOString().split("T")[0];

      const response = await getReportsData(id, formattedFrom, formattedTo);
      console.log("response", response);
      if (response.success) {
        setReportData(response.reportsData || []);
      } else {
        setReportData(response.reportsData || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      fetchReport();
      hasFetched.current = true;
    }
  }, []);

  // useEffect(() => {
  //   if (!fromDate) return;

  //   const newMaxToDate = new Date(fromDate);
  //   newMaxToDate.setMonth(newMaxToDate.getMonth() + 1);
  //   newMaxToDate.setDate(newMaxToDate.getDate() - 1);

  //   const today = new Date();
  //   setMaxToDate(newMaxToDate > today ? today : newMaxToDate);

  //   // Auto-correct toDate if it's outside valid range
  //   if (toDate > newMaxToDate) {
  //     setToDate(newMaxToDate > today ? today : newMaxToDate);
  //   }
  //   console.log("fromDate FKM" , fromDate);
  //   console.log("toDate FKM ", toDate);
  // }, [fromDate]);
  useEffect(() => {
    if (!fromDate) return;

    const oneMonthLater = new Date(fromDate);
    oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
    oneMonthLater.setDate(oneMonthLater.getDate() - 1);

    const today = new Date();
    const maxValidToDate = oneMonthLater > today ? today : oneMonthLater;
    setMaxToDate(maxValidToDate);

    // Auto-correct toDate if it's outside valid range
    if (toDate && (toDate < fromDate || toDate > maxValidToDate)) {
      setToDate(maxValidToDate);
    }
  }, [fromDate]);
  const handleFilterDays = (days: number) => {
    const now = new Date();
    const past = new Date(now);
    past.setDate(now.getDate() - days + 1);
    setFromDate(past);
    setToDate(now);
    setTimeout(fetchReport, 0);
  };

  const labels = reportData.map((item) => formatDate(item.log_date));
  const diffDays = Math.ceil(
    (toDate.getTime() - fromDate.getTime()) / (1000 * 3600 * 24)
  );
  const showMonthOnly = diffDays > 30;

  // const labels = reportData.map((item) =>
  //   showMonthOnly
  //     ? new Date(item.log_date).toLocaleDateString("en-GB", { month: "short" })
  //     : formatDate(item.log_date)
  // );

  const onback = () => {
    navigate("/experience/list");
  };

  const data = {
    labels,
    datasets: [
      {
        label: "Impression Count",
        data: reportData.map((item) => Number(item.impression_count)),
        backgroundColor: "Violet",
      },
      {
        label: "Close Count",
        data: reportData.map((item) => Number(item.close_count)),
        backgroundColor: "MediumSlateBlue",
      },
      {
        label: "Click Count",
        data: reportData.map((item) => Number(item.click_count)),
        backgroundColor: "DodgerBlue",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
    },
    scales: {
      x: {
        stacked: true,
        title: { display: true, text: "Date" },
        ticks: {
          maxRotation: 45,
          autoSkip: false,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: "Count",
        },
      },
    },
  };
  const totalRecords = reportData.length;

  const totalClicks = reportData.reduce(
    (sum, item) => sum + Number(item.click_count),
    0
  );
  const totalCloses = reportData.reduce(
    (sum, item) => sum + Number(item.close_count),
    0
  );
  const totalImpressions = reportData.reduce(
    (sum, item) => sum + Number(item.impression_count),
    0
  );

  const clickRate = totalRecords ? (totalClicks / totalRecords).toFixed(2) : 0;
  const closeRate = totalRecords ? (totalCloses / totalRecords).toFixed(2) : 0;
 const ctr = totalClicks > 0 && totalImpressions > 0
  ? Number(((totalClicks / totalImpressions) * 100).toFixed(2))
  : 0;

  const pieData = {
    labels: ["Total Impression Count", "Total Close Count", "Total Click Count"],
    datasets: [
      {
        data: [totalImpressions, totalCloses, totalClicks],
        backgroundColor: ["Violet", "MediumSlateBlue", "DodgerBlue"],
        hoverOffset: 10,
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="dashboard">
      <h2>Activity Report </h2>
      <Grid container className="templates__header">
        <Grid
          size={{ xs: 12, md: 12, sm: 6 }}
          className="templates__header--search"
        >
          <div className="d-center">
            <span className="Icon">
              <IconButton aria-label="close" onClick={onback}>
                <ArrowbackIcon />
              </IconButton>{" "}
            </span>{" "}
            <h2> {experiencName}</h2>
          </div>{" "}
        </Grid>
      </Grid>
      <Grid container className="templates__header">
        <Grid
          size={{ xs: 12, md: 3, sm: 6 }}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          className="templates__header--search"
        >
          <span className="report_datepicker--label">From:</span>
          <DatePicker
            className="report_datepicker"
            selected={fromDate}
            onChange={(date: Date | null) => {
              if (date) setFromDate(date);
            }}
            dateFormat="yyyy-MM-dd"
            maxDate={new Date()} // Optional: restrict selection to today or earlier
          />
        </Grid>
        <Grid
          size={{ xs: 12, md: 3, sm: 6 }}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          className="templates__header--search"
        >
          <span className="report_datepicker--label">To:</span>
          <DatePicker
            className="report_datepicker"
            selected={toDate}
            onChange={(date: Date | null) => {
              if (date) setToDate(date);
            }}
            dateFormat="yyyy-MM-dd"
            maxDate={maxToDate}
            minDate={fromDate} // ✅ Optional: Prevent selecting before fromDate
          />
        </Grid>
        <Grid
          size={{ xs: 12, md: 2, sm: 4 }}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          className="templates__header--button br-1"
        >
          <Button
            sx={{
              "--variant-containedBg": "rgba(228, 0, 43, 1)",
            }}
            fullWidth
            variant="contained"
            color="error"
            onClick={() => handleFilterDays(7)}
          >
            Last 7 Days
          </Button>
        </Grid>
        <Grid
          size={{ xs: 12, md: 2, sm: 4 }}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          className="templates__header--button br-1"
        >
          <Button
            sx={{
              "--variant-containedBg": "rgba(228, 0, 43, 1)",
            }}
            fullWidth
            variant="contained"
            color="error"
            onClick={() => handleFilterDays(30)}
          >
            Last 30 Days
          </Button>
        </Grid>

        <Grid
          size={{ xs: 12, md: 2, sm: 4 }}
          className="templates__header--button wd-100"
        >
          <Button
            fullWidth
            sx={{
              "--variant-containedBg": "rgba(228, 0, 43, 1)",
            }}
            variant="contained"
            color="error"
            onClick={fetchReport}
          >
            Apply
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <span className="loader" />
      ) : (
        <>
          <Box>
            <div className="graph">
              {reportData?.length > 0 &&
                (Number(clickRate) > 0 || Number(closeRate) > 0) && (
                  <div className="graph__pie">
                    <div className="graph__pie--chart">
                      <Pie data={pieData} />
                    </div>

                    <div className="graph_pie--data">
                      <div style={{ marginTop: 20 }}>
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                        >
                          <p style={{ color: "Violet", fontWeight: "bold" }}>
                            Total Impression Count:{" "}
                            <CountUp
                              end={Number(totalImpressions)}
                              
                            />
                          </p>
                          <p
                            style={{
                              color: "MediumSlateBlue",
                              fontWeight: "bold",
                            }}
                          >
                            Total Close Count:{" "}
                            <CountUp end={Number(totalCloses)} />
                          </p>
                          <p
                            style={{ color: "DodgerBlue", fontWeight: "bold" }}
                          >
                            Total Click Count:{" "}
                            <CountUp end={Number(totalClicks)} />
                          </p>
                          <p
                            style={{ color: "red", fontWeight: "bold" }}
                          >
                            CTR:{" "}
                            <CountUp end={Number(ctr)} />
                          %</p>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                )}

              <div className="graph__bar--chart">
                {reportData && reportData.length > 0 ? (
                  <Bar data={data} options={options} />
                ) : (
                  <p style={{ textAlign: "center" }}>
                    No data available for the selected date range
                  </p>
                )}
              </div>
            </div>
          </Box>
        </>
      )}
    </div>
  );
}

export default Report;
